/**
 * BuildBoxCart.js — "Your Cart" panel after Continue (monthly / one-off).
 *
 * Parity with old createBuildBoxCartSummaryHTML (ss4 monthly, ss5 one-off).
 */

import { emit, on, getState } from "../../../../core/state.js";
import {
  computeBuildBoxPricing,
  expandCartToBottles,
  getBuildBoxShippingMessage,
  getDiscountPercent,
  getBuildBoxState,
} from "../../features/wine-box.js";

const TRASH_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 4.00004H14M12.6667 4.00004V13.3334C12.6667 14 12 14.6667 11.3333 14.6667H4.66667C4 14.6667 3.33333 14 3.33333 13.3334V4.00004M5.33333 4.00004V2.66671C5.33333 2.00004 6 1.33337 6.66667 1.33337H9.33333C10 1.33337 10.6667 2.00004 10.6667 2.66671V4.00004" stroke="#0A0A0A" stroke-width="1.33" stroke-linecap="round" stroke-linejoin="round"/></svg>';

const esc = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

function frequencyHTML(active) {
  return `
    <div class="wine-box-frequency" role="group" aria-label="Delivery frequency">
      <button type="button" class="wine-box-frequency-btn${active === "monthly" ? " is-active" : ""}" data-build-box-cart-frequency="monthly" aria-pressed="${active === "monthly"}">Monthly</button>
      <button type="button" class="wine-box-frequency-btn${active === "oneoff" ? " is-active" : ""}" data-build-box-cart-frequency="oneoff" aria-pressed="${active === "oneoff"}">One-off</button>
    </div>
  `;
}

function qtyControlsHTML(variantId, quantity) {
  return `
    <div class="product-action">
      <button type="button" class="delete-cart" aria-label="delete from basket" title="delete from basket" data-variant-id="${esc(variantId)}">
        ${TRASH_ICON}
      </button>
      <div class="product-quantity-wrapper">
        <button type="button" class="dec-qty" aria-label="Decrease quantity" data-variant-id="${esc(variantId)}"><span aria-hidden="true">−</span></button>
        <div class="cart-qty">${quantity}</div>
        <button type="button" class="inc-qty" aria-label="Increase quantity" data-variant-id="${esc(variantId)}"><span aria-hidden="true">+</span></button>
      </div>
    </div>
  `;
}

function savingsJourneyHTML(bottleCount, freeShippingUnlocked) {
  const milestones = [
    { label: "Saving 5%" },
    { label: "Free Shipping" },
    { label: "Saving 10%" },
    { label: "Saving 15%" },
  ];
  const activeMilestones = [
    bottleCount >= 3,
    !!freeShippingUnlocked,
    bottleCount >= 6,
    bottleCount >= 12,
  ];
  let furthestActiveIdx = -1;
  activeMilestones.forEach((a, i) => {
    if (a) furthestActiveIdx = i;
  });
  let progressPercent = 0;
  if (furthestActiveIdx === 0) progressPercent = 12.5;
  else if (furthestActiveIdx === 1) progressPercent = 37.5;
  else if (furthestActiveIdx === 2) progressPercent = 62.5;
  else if (furthestActiveIdx === 3) progressPercent = 100;

  const markers = milestones
    .map((m, idx) => {
      const isActive = activeMilestones[idx];
      const isCurrent = idx === furthestActiveIdx;
      return `<div class="build-box-journey-marker${isActive ? " is-active" : ""}${isCurrent ? " is-current" : ""}">
        <span class="build-box-journey-dot"></span>
        <span class="build-box-journey-label">${m.label}</span>
      </div>`;
    })
    .join("");

  return `
    <div class="build-box-journey">
      <div class="build-box-journey-header">
        <span>Your savings journey :</span>
        <span class="build-box-journey-total">Total Saving : ${getDiscountPercent(bottleCount)}%</span>
      </div>
      <div class="build-box-journey-track-wrap">
        <div class="build-box-journey-track">
          <div class="build-box-journey-fill" style="width:${progressPercent}%"></div>
        </div>
        <div class="build-box-journey-markers">${markers}</div>
      </div>
    </div>
  `;
}

function itemsHTML(cartItems) {
  return (cartItems || [])
    .map(
      (p) => `<div class="build-box-cart-item cart-product">
        <div class="build-box-cart-item-image">
          <img src="${esc(p.image || "")}" alt="${esc(p.productTitle || "")}">
        </div>
        <div class="build-box-cart-item-body">
          <p class="build-box-cart-item-title">${esc(p.productTitle || "")}</p>
          <p class="build-box-cart-item-price">
            <strong>${esc(p.price || "")}</strong>
            ${p.isDiscounted && p.originalPrice ? `<s>${esc(p.originalPrice)}</s>` : ""}
          </p>
          <div class="build-box-cart-item-actions">
            ${qtyControlsHTML(p.productVariantId, p.quantity ?? 1)}
          </div>
        </div>
      </div>`,
    )
    .join("");
}

function bodyHTML({ items, cartSummary, frequency, tier }) {
  const isMonthly = frequency === "monthly";
  const pricing = computeBuildBoxPricing(items, { isMonthly });
  const checkoutDisabled = pricing.bottleCount < 1;

  return `
    <div class="build-box-cart-header">
      <h3 class="build-box-cart-title">Your Cart</h3>
      ${frequencyHTML(frequency)}
    </div>
    <div class="build-box-cart-items-scroll">
      ${itemsHTML(items)}
    </div>
    ${
      isMonthly
        ? `<div class="build-box-size-section">
            <div class="build-box-size-header">
              <span>BOX SIZE</span>
              <button type="button" class="build-box-edit-size" data-build-box-edit-size>Edit box size</button>
            </div>
            <div class="build-box-size-card">
              <span>${pricing.bottleCount} Bottles</span>
              <span class="build-box-size-discount">${
                pricing.discountPercent > 0
                  ? `Save ${pricing.discountPercent}% Applied`
                  : "No Discount Applied"
              }</span>
            </div>
          </div>`
        : ""
    }
    <div class="build-box-coupon-section">
      <input type="text" class="build-box-coupon-input" placeholder="Enter coupon code" />
      <button type="button" class="build-box-coupon-apply" data-coupon-apply-btn>Apply</button>
    </div>
    ${
      isMonthly
        ? savingsJourneyHTML(
            pricing.bottleCount,
            cartSummary?.freeShippingUnlocked,
          )
        : ""
    }
    <div class="build-box-cart-footer">
      <div class="build-box-cart-footer-main">
        <div class="build-box-cart-subtotal-row">
          <span class="build-box-cart-subtotal-label">Subtotal</span>
          <span class="build-box-cart-subtotal-prices">
            ${pricing.subtotalWas ? `<s class="build-box-cart-subtotal-was">${pricing.subtotalWas}</s>` : ""}
            <strong class="build-box-cart-subtotal-now">${pricing.subtotalDisplay}</strong>
          </span>
        </div>
        <p class="build-box-cart-shipping">${getBuildBoxShippingMessage(cartSummary)}</p>
      </div>
      <button type="button" class="build-box-checkout-btn" data-proceed-checkout${checkoutDisabled ? " disabled" : ""}>Process to checkout</button>
    </div>
  `;
}

export default class BuildBoxCart {
  render(message) {
    const wrap = document.createElement("div");
    wrap.className = "message bot-message build-box-cart-message";

    const data = message.data || {};
    let frequency = data.frequency || getBuildBoxState().summaryFrequency || "oneoff";
    let tier = data.tier || getBuildBoxState().tier || 3;

    const container = document.createElement("div");
    container.className = `message-content build-box-cart-container cart-container is-${frequency}`;
    container.dataset.buildBoxTier = String(tier);
    container.dataset.messageId = message.id || "";

    const paint = () => {
      const items = getState().cart?.items?.length
        ? getState().cart.items
        : data.items || data.cart?.items || [];
      const cartSummary =
        getState().cart?.summary || data.cart?.cart || data.cart || {};
      const bottleCount = expandCartToBottles(items).length;
      if (bottleCount >= 12) tier = 12;
      else if (bottleCount >= 6) tier = 6;
      else tier = 3;
      container.dataset.buildBoxTier = String(tier);
      container.classList.toggle("is-monthly", frequency === "monthly");
      container.classList.toggle("is-oneoff", frequency === "oneoff");
      container.innerHTML = bodyHTML({
        items,
        cartSummary,
        frequency,
        tier,
      });
    };

    paint();

    container.addEventListener("click", (e) => {
      const freqBtn = e.target.closest("[data-build-box-cart-frequency]");
      if (freqBtn) {
        frequency = freqBtn.dataset.buildBoxCartFrequency;
        emit("ws-build-box-cart-frequency", { frequency });
        paint();
        return;
      }
      if (e.target.closest("[data-build-box-edit-size]")) {
        emit("ws-build-box-edit-size");
        return;
      }
      if (e.target.closest("[data-proceed-checkout]")) {
        const btn = e.target.closest("[data-proceed-checkout]");
        if (btn.disabled) return;
        emit("ws-checkout");
        return;
      }
      const del = e.target.closest(".delete-cart");
      if (del) {
        const cartProduct = del.closest('.cart-product');
        const overlay = document.createElement("div");
        overlay.className = "ws-cart-overlay";
        cartProduct.appendChild(overlay);
        emit("ws-cart-remove", {
          variantId: del.dataset.variantId,
          fromCart: true,
        });
        return;
      }
      const inc = e.target.closest(".inc-qty");
      if (inc) {
        const variantId = inc.dataset.variantId;
        const cartProduct = inc.closest('.cart-product');
        const overlay = document.createElement("div");
        overlay.className = "ws-cart-overlay";
        cartProduct.appendChild(overlay);
        const line = (getState().cart?.items || []).find(
          (it) => String(it.productVariantId) === String(variantId),
        );
        const qty = Math.min(12, (Number(line?.quantity) || 1) + 1);
        emit("ws-cart-update", { variantId, quantity: qty, fromCart: true });
        return;
      }
      const dec = e.target.closest(".dec-qty");
      if (dec) {
        const variantId = dec.dataset.variantId;
        const cartProduct = dec.closest('.cart-product');
        const overlay = document.createElement("div");
        overlay.className = "ws-cart-overlay";
        cartProduct.appendChild(overlay);
        const line = (getState().cart?.items || []).find(
          (it) => String(it.productVariantId) === String(variantId),
        );
        const qty = (Number(line?.quantity) || 1) - 1;
        if (qty <= 0) {
          emit("ws-cart-remove", { variantId, fromCart: true });
        } else {
          emit("ws-cart-update", { variantId, quantity: qty, fromCart: true });
        }
      }
    });

    on("ws-cart-count", () => {
      if (container.isConnected) paint();
    });
    on("ws-build-box-cart-rerender", ({ frequency: freq } = {}) => {
      if (freq) frequency = freq;
      if (container.isConnected) paint();
    });

    wrap.appendChild(container);
    return wrap;
  }
}
